import { useCallback, useEffect, useRef, useState } from "react";
import { api, API_BASE, authToken } from "@/lib/api";

/** Connie's conversation state + the streamed reply (SSE over fetch, so the auth header rides along). */
export function useConnie() {
  const [me, setMe] = useState(null);
  const [messages, setMessages] = useState([]);
  const [sessionId, setSessionId] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const abort = useRef(null);

  const load = useCallback(async (sid) => {
    try {
      const r = await api.get("/connie/me", { params: sid ? { session_id: sid } : {} });
      setMe(r.data);
      setMessages(r.data.messages || []);
      setSessionId(r.data.session_id || null);
      setError(null);
    } catch (e) { setError(e?.response?.data?.detail || "Connie isn't available on this account"); }
  }, []);

  useEffect(() => { load(); return () => abort.current?.abort(); }, [load]);

  const patchLast = useCallback((fn) => setMessages((m) => {
    const copy = [...m];
    copy[copy.length - 1] = fn(copy[copy.length - 1]);
    return copy;
  }), []);

  const handle = useCallback((evt) => {
    if (evt.t === "start") setSessionId(evt.session_id);
    else if (evt.t === "delta") patchLast((x) => ({ ...x, text: (x.text || "") + evt.c }));
    else if (evt.t === "tool") patchLast((x) => ({ ...x, live: [...(x.live || []), { name: evt.name, label: evt.label }] }));
    else if (evt.t === "done") patchLast((x) => ({ ...x, id: evt.id, text: evt.text || x.text, tools: evt.tools, streaming: false }));
  }, [patchLast]);

  const send = useCallback(async (text) => {
    const body = (text || "").trim();
    if (!body || busy) return;
    setBusy(true);
    const now = new Date().toISOString();
    setMessages((m) => [...m, { id: `u-${now}`, role: "user", text: body, created_at: now },
                             { id: `a-${now}`, role: "assistant", text: "", streaming: true, live: [], created_at: now }]);
    const ctrl = new AbortController();
    abort.current = ctrl;
    try {
      const token = authToken();
      const res = await fetch(`${API_BASE}/connie/me/chat`, {
        method: "POST", credentials: "include", signal: ctrl.signal,
        headers: { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify({ text: body, session_id: sessionId }),
      });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        throw new Error(d.detail || "Connie couldn't answer just now");
      }
      const reader = res.body.getReader();
      const dec = new TextDecoder();
      let buf = "";
      for (;;) {
        const { value, done } = await reader.read();
        if (done) break;
        buf += dec.decode(value, { stream: true });
        const parts = buf.split("\n\n");
        buf = parts.pop();
        parts.forEach((p) => {
          const line = p.split("\n").find((l) => l.startsWith("data: "));
          if (line) { try { handle(JSON.parse(line.slice(6))); } catch { /* partial */ } }
        });
      }
    } catch (e) {
      if (e.name !== "AbortError") {
        patchLast((x) => ({ ...x, text: x.text || e.message || "Something went wrong — try again in a moment.", streaming: false, failed: true }));
      }
    } finally {
      patchLast((x) => (x?.role === "assistant" ? { ...x, streaming: false } : x));
      setBusy(false);
    }
  }, [busy, sessionId, handle, patchLast]);

  const fresh = useCallback(async () => {
    if (busy) return;
    const r = await api.post("/connie/me/sessions");
    setSessionId(r.data.session_id);
    setMessages([]);
  }, [busy]);

  return { me, messages, sessionId, busy, error, send, fresh, open: load };
}
